import { Download } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { useFeature } from '../../../modules/add-ons/index.js'
import { Button, HelpIcon, LoadFailure, Skeleton, useEndpointHints, useFeatures, useLoadingWindow, useRememberedTab, type SessionRole } from '../../../modules/core/index.js'
import { useFacilityContext } from '../../../modules/facilities/index.js'
import { downloadMonthsCsv, spanEnding, useInsightsMonths, useInsightsPortfolio } from '../hooks.js'
import { FEATURE_KEY, FEATURE_NAME, PAGE_SEGMENT, monthLabel } from '../labels.js'
import { SPAN_CHOICES, type InsightsPortfolio, type SpanMonths } from '../types.js'
import { AttendanceScreen } from './AttendanceScreen.js'
import { CensusScreen } from './CensusScreen.js'
import { CoverageScreen } from './CoverageScreen.js'
import { EMPTY_SLOTS, chosenRowsOf, slotsFrom, validSlots, type ChosenSlots } from './FacilityChoice.js'
import { PortfolioScreen } from './PortfolioScreen.js'
import { QuarterScreen } from './QuarterScreen.js'
import { ReliabilityScreen } from './ReliabilityScreen.js'
import { TiersScreen } from './TiersScreen.js'
import '../print.css'

const TABS = ['portfolio', 'reliability', 'tiers', 'attendance', 'coverage', 'census', 'quarter'] as const
type Tab = (typeof TABS)[number]
const TAB_LABELS: Record<Tab, string> = { portfolio: 'Portfolio', reliability: 'Reliability', tiers: 'Tiers', attendance: 'Attendance', coverage: 'Coverage', census: 'Census and workforce', quarter: 'Last quarter' }
/** The tallies each screen draws, so a months read carries no more. */
const TAB_FIELDS: Partial<Record<Tab, readonly string[]>> = { tiers: ['tiers', 'tierChanges'], attendance: ['outcomes'], coverage: ['coverage', 'workforce'], census: ['census', 'staffing', 'workforce'] }
const TAB_STORAGE_KEY = 'insights-tab'
const SPAN_STORAGE_KEY = 'insights-span'

function rememberedSpan(): SpanMonths {
  try {
    const stored = Number(localStorage.getItem(SPAN_STORAGE_KEY))
    return (SPAN_CHOICES as readonly number[]).includes(stored) ? (stored as SpanMonths) : 12
  } catch {
    return 12
  }
}

/**
 * The Insights page (docs/plans/executive-insights-plan.md section 6): the client's view rather than a facility's,
 * opening on the portfolio with a span, a CSV of the months and the screens as sub-tabs. It gates itself on the
 * client's license, since the add-on has no switch per facility. Every screen opens with a summary before the detail.
 */
export function InsightsPage({ role }: { role: SessionRole }) {
  const standing = useFeature(null, FEATURE_KEY)
  const tab = useRememberedTab<Tab>(TAB_STORAGE_KEY, TABS)
  const [months, setMonths] = useState<SpanMonths>(rememberedSpan)
  const [downloading, setDownloading] = useState(false)
  const [downloadFailed, setDownloadFailed] = useState(false)
  // The facilities drawn as lines of their own on the Reliability and Attendance screens, kept across the tabs.
  const [slots, setSlots] = useState<ChosenSlots>(EMPTY_SLOTS)
  const { total: facilityCount } = useFacilityContext()
  const showEndpoints = useEndpointHints()
  // Other add-ons' actions for this page (the AI summary), from the registry, so this page knows no add-on by name.
  const pageActions = useFeatures().flatMap((feature) => (feature.pageActions ?? []).filter((entry) => entry.page === PAGE_SEGMENT).map((entry) => ({ key: `${feature.key}-${entry.page}`, Action: entry.action })))
  const portfolio = useInsightsPortfolio(months, standing.licensed)
  const span = useMemo(() => spanEnding(new Date(), months), [months])
  const fields = TAB_FIELDS[tab.current] ?? null
  const needsMonths = fields !== null && standing.licensed && portfolio.isSuccess
  const monthsQuery = useInsightsMonths(span.from, span.to, null, fields, needsMonths)
  useLoadingWindow({
    shown: standing.licensed,
    visit: `insights:${months}:${tab.current}`,
    title: `Loading ${TAB_LABELS[tab.current].toLowerCase()}`,
    sources: [
      { key: 'portfolio', label: 'Every facility, month by month', query: portfolio },
      { key: 'months', label: 'The months in detail', query: monthsQuery, skip: !needsMonths },
    ],
  })
  // While the page is open the print stylesheet applies: the browser's Save as PDF is the board pack (plan D7).
  useEffect(() => {
    document.body.classList.add('printing-insights')
    return () => document.body.classList.remove('printing-insights')
  }, [])
  // The first three facilities are drawn until the person chooses; a facility that leaves the portfolio leaves its slot.
  useEffect(() => {
    if (portfolio.data && slots.every((slot) => slot === null)) {
      setSlots(slotsFrom(portfolio.data.facilities))
    }
  }, [portfolio.data, slots])

  const chooseSpan = (value: SpanMonths): void => {
    setMonths(value)
    try {
      localStorage.setItem(SPAN_STORAGE_KEY, String(value))
    } catch {
      // A browser that keeps nothing still gets the span for this visit.
    }
  }
  const download = async (): Promise<void> => {
    setDownloading(true)
    setDownloadFailed(false)
    try {
      await downloadMonthsCsv(span.from, span.to)
    } catch {
      setDownloadFailed(true)
    } finally {
      setDownloading(false)
    }
  }
  const facilityHref = (facilityId: string): string => (facilityCount > 1 ? `/facilities/${encodeURIComponent(facilityId)}` : '/')

  if (standing.isLoading) {
    return <Skeleton lines={5} label={`Loading ${FEATURE_NAME}`} className="mx-auto max-w-5xl p-8" />
  }
  if (!standing.licensed) {
    return (
      <div className="mx-auto max-w-5xl p-8 text-sm text-ink-mid">
        <p>
          <span className="font-bold text-ink">{FEATURE_NAME} is not part of your agreement yet.</span> It shows how every facility is doing over time: scores, tiers, attendance, coverage, census and workforce, month by month.{' '}
          {role === 'operator' ? 'Ask SeeWhoCares about adding it.' : 'Your operator can ask SeeWhoCares about adding it.'}
        </p>
      </div>
    )
  }

  return (
    <div className="insights-page mx-auto flex max-w-5xl flex-col gap-5 px-7 py-6">
      <div className="flex flex-wrap items-end gap-4">
        <div className="min-w-0 flex-1">
          <h1 className="flex items-center gap-2 text-xl font-black text-ink">
            {FEATURE_NAME} <HelpIcon topic={`executive_insights.${tab.current}`} />
          </h1>
          <p className="mt-0.5 text-xs text-ink-mid">
            {role === 'facility_administrator' ? 'Your facilities' : 'Every facility'} over time, from what the platform keeps · {monthLabel(span.from, 'long')} to {monthLabel(span.to, 'long')}
          </p>
        </div>
        <div className="no-print flex flex-wrap items-center gap-2">
          <label className="flex items-center gap-2 text-[11px] text-ink-mid">
            Span
            <select value={months} onChange={(event) => chooseSpan(Number(event.target.value) as SpanMonths)} className="rounded border border-marine/30 bg-white px-2 py-1.5 text-xs text-ink">
              {SPAN_CHOICES.map((choice) => (
                <option key={choice} value={choice}>
                  {choice} months
                </option>
              ))}
            </select>
          </label>
          <Button icon={Download} variant="secondary" size="sm" busy={downloading} onClick={() => void download()}>
            {downloading ? 'Preparing the CSV…' : 'CSV'}
          </Button>
          {pageActions.map(({ key, Action }) => (
            <Action key={key} role={role} />
          ))}
          {downloadFailed && <LoadFailure size="xs" message="The CSV could not be prepared." onRetry={() => void download()} retrying={downloading} />}
        </div>
      </div>

      <nav aria-label="Insights screens" className="no-print flex flex-wrap gap-1 border-b border-marine/15">
        {TABS.map((id) => (
          <button key={id} type="button" aria-current={tab.current === id ? 'page' : undefined} onClick={() => tab.choose(id)} className={`-mb-px border-b-2 px-3 py-2 text-xs font-bold ${tab.current === id ? 'border-marine text-marine' : 'border-transparent text-ink-mid hover:text-ink'}`}>
            {TAB_LABELS[id]}
          </button>
        ))}
      </nav>

      {tab.current === 'quarter' ? (
        <QuarterScreen showEndpoints={showEndpoints} />
      ) : portfolio.isPending ? (
        <Skeleton lines={6} label="Loading the portfolio" />
      ) : portfolio.isError ? (
        <LoadFailure message="The portfolio could not be loaded." onRetry={() => void portfolio.refetch()} retrying={portfolio.isFetching} />
      ) : portfolio.data.facilities.every((row) => row.latest === null) ? (
        <NothingYet />
      ) : tab.current === 'portfolio' ? (
        <PortfolioScreen portfolio={portfolio.data} facilityHref={facilityHref} />
      ) : tab.current === 'reliability' ? (
        <ReliabilityScreen portfolio={portfolio.data} span={span} slots={slots} onSlots={setSlots} showEndpoints={showEndpoints} />
      ) : monthsQuery.isPending ? (
        <Skeleton lines={6} label="Loading the months" />
      ) : monthsQuery.isError ? (
        <LoadFailure message="The months could not be loaded." onRetry={() => void monthsQuery.refetch()} retrying={monthsQuery.isFetching} />
      ) : (
        <DetailScreen tab={tab.current} portfolio={portfolio.data} rollups={monthsQuery.data} slots={slots} />
      )}
    </div>
  )
}

function DetailScreen({ tab, portfolio, rollups, slots }: { tab: Tab; portfolio: InsightsPortfolio; rollups: Parameters<typeof TiersScreen>[0]['rollups']; slots: ChosenSlots }) {
  const chosen = chosenRowsOf(validSlots(slots, portfolio.facilities), portfolio.facilities).map((entry) => entry.row)
  switch (tab) {
    case 'tiers':
      return <TiersScreen rollups={rollups} periods={portfolio.periods} />
    case 'attendance':
      return <AttendanceScreen rollups={rollups} periods={portfolio.periods} chosen={chosen} rows={portfolio.facilities} />
    case 'coverage':
      return <CoverageScreen rollups={rollups} periods={portfolio.periods} rows={portfolio.facilities} />
    case 'census':
      return <CensusScreen rollups={rollups} periods={portfolio.periods} rows={portfolio.facilities} />
    default:
      return null
  }
}

function NothingYet() {
  return (
    <div className="rounded-lg border border-marine/15 bg-white px-4 py-8 text-center text-sm text-ink-mid">
      <p className="font-bold text-ink">Nothing counted yet.</p>
      <p className="mt-1 text-xs">A facility's month is added up once its scoring period closes and each day after a release. Trends begin after the second month.</p>
    </div>
  )
}
